import { useState } from "react";
import { Mail, Linkedin, CheckCircle2, Download } from "lucide-react";
import { PROFILE } from "../shared/Brand";

// Contact Section
export const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit form');
      }
      
      setSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
      // Error logged for debugging - consider adding error tracking service
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-12 sm:py-16 lg:py-20 bg-[#0A0A0A] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <div className="text-xs sm:text-sm font-mono text-[#D4AF37] tracking-widest uppercase mb-3 sm:mb-4">Contact</div>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-semibold text-white tracking-tight leading-tight mb-4 sm:mb-6">
              Let's Build <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#E8C547]">Something</span>
            </h2>
            <p className="text-sm sm:text-base text-zinc-300 leading-relaxed mb-8 sm:mb-10">
              I'm always interested in meeting thoughtful founders, CROs, sales leaders, and teams building ambitious revenue organizations.
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-8">
              <a href={PROFILE.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#D4AF37] text-[#0A0A0A] rounded-md font-semibold hover:bg-[#E8C547] transition-colors" data-testid="contact-linkedin-btn">
                <Linkedin className="w-4 h-4" /> Connect on LinkedIn
              </a>
              <a href={`mailto:${PROFILE.email}`} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-[#D4AF37]/50 text-[#D4AF37] rounded-md font-medium hover:bg-[#D4AF37]/10 transition-colors" data-testid="contact-email-btn">
                <Mail className="w-4 h-4" /> Email Dan
              </a>
              <a href={PROFILE.resume} download className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-[#D4AF37]/50 text-[#D4AF37] rounded-md font-medium hover:bg-[#D4AF37]/10 transition-colors" data-testid="contact-resume-btn">
                <Download className="w-4 h-4" /> Download Resume
              </a>
            </div>
            <p className="text-sm text-zinc-400" data-testid="contact-email-text">{PROFILE.email}</p>
          </div>

          {/* Contact Form */}
          <div className="bg-[#1C1C1C] rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:p-10 border border-white/5">
            <h3 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6">Start a Conversation</h3>
            
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-12 sm:py-16 text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-7 h-7 sm:w-8 sm:h-8 text-[#D4AF37]" />
                </div>
                <h4 className="text-lg sm:text-xl font-semibold text-white mb-2">Message Sent!</h4>
                <p className="text-zinc-300 text-sm sm:text-base">We'll be in touch shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div>
                  <label className="block text-xs sm:text-sm text-zinc-300 mb-2">Name</label>
                  <input 
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#D4AF37]/50 transition-colors text-sm sm:text-base"
                    placeholder="Your name"
                    data-testid="input-name"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm text-zinc-300 mb-2">Email</label>
                  <input 
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#D4AF37]/50 transition-colors text-sm sm:text-base"
                    placeholder="your@email.com"
                    data-testid="input-email"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm text-zinc-300 mb-2">Message</label>
                  <textarea 
                    required
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#D4AF37]/50 transition-colors resize-none text-sm sm:text-base"
                    placeholder="What can I help you with?"
                    data-testid="input-message"
                    disabled={loading}
                  />
                </div>
                
                {error && (
                  <p className="text-xs sm:text-sm text-red-400">{error}</p>
                )}
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 sm:py-4 bg-[#D4AF37] text-[#0A0A0A] rounded-xl font-semibold hover:bg-[#E8C547] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base"
                  data-testid="submit-contact"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-[#0A0A0A]/30 border-t-[#0A0A0A] rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
